/**
 * CMS catalog from GET /products/parents (parent rows with nested `products[]`).
 * Produces the same flat `products[]` shape the storefront previously built from GET /products
 * (each item has `category` from parent + `translated.title` / `translated.description`).
 */

export function flattenProductsApiData(data) {
    if (!Array.isArray(data)) return [];
    const out = [];
    for (const row of data) {
        if (!row || typeof row !== 'object') continue;
        const nested = row.products;
        if (Array.isArray(nested) && nested.length) {
            const parentCategory = {
                id: row.id,
                path: row.path,
                value: row.value,
                thumbnail: row.thumbnail,
                translated: row.translated,
                description: row.description,
            };
            for (const p of nested) {
                if (!p || typeof p !== 'object') continue;
                const childCat = p.category && typeof p.category === 'object' ? p.category : {};
                out.push({
                    ...p,
                    category: {
                        ...parentCategory,
                        ...childCat,
                    },
                });
            }
            continue;
        }
        out.push(row);
    }
    return out;
}

/** Parents API nests title/description on the product; flat /products used translated.* — align shapes. */
export function ensureProductTranslatedShape(product) {
    if (!product || typeof product !== 'object') return product;
    const t = product.translated && typeof product.translated === 'object' ? { ...product.translated } : {};
    const title = t.title ?? product.title ?? '';
    const description = t.description ?? product.description ?? '';
    return {
        ...product,
        translated: { ...t, title, description },
    };
}

/** Group flat GET /products rows into parent buckets (products[] per category). */
export function categorizeFlatProducts(flatProducts) {
    const byCategory = new Map();
    for (const product of flatProducts) {
        if (!product || typeof product !== 'object') continue;
        const cat = product.category && typeof product.category === 'object' ? product.category : {};
        const catId = cat.id ?? product.category_id ?? null;
        if (catId == null || catId === '') continue;
        const key = String(catId);
        if (!byCategory.has(key)) {
            byCategory.set(key, {
                id: cat.id ?? catId,
                path: cat.path,
                value: cat.value,
                thumbnail: cat.thumbnail,
                translated: cat.translated,
                description: cat.description,
                products: [],
            });
        }
        byCategory.get(key).products.push(product);
    }
    return Array.from(byCategory.values());
}

export function normalizeParentsCatalogResponse(responseData) {
    const raw = responseData?.data || [];
    const categorizedProducts = Array.isArray(raw) ? raw : [];
    const products = flattenProductsApiData(raw).map(ensureProductTranslatedShape);

    const rawItems = responseData?.items;
    let productIconItems = [];
    if (Array.isArray(rawItems)) {
        productIconItems = rawItems;
    } else if (rawItems && typeof rawItems === 'object') {
        productIconItems = Object.values(rawItems).flatMap((v) =>
            Array.isArray(v) ? v : []
        );
    }

    return { categorizedProducts, products, productIconItems };
}

/** GET /products?currency=… — flat list with per-product category + currency-priced amounts. */
export function normalizeProductsCatalogResponse(responseData) {
    const raw = responseData?.data || [];
    const products = (Array.isArray(raw) ? raw : []).map(ensureProductTranslatedShape);
    const categorizedProducts = categorizeFlatProducts(products);

    const rawItems = responseData?.items;
    let productIconItems = [];
    if (Array.isArray(rawItems)) {
        productIconItems = rawItems;
    } else if (rawItems && typeof rawItems === 'object') {
        productIconItems = Object.values(rawItems).flatMap((v) =>
            Array.isArray(v) ? v : []
        );
    }

    return { categorizedProducts, products, productIconItems };
}
